import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ModeToggle } from '@/components/layout/ModeToggle';
import { LayoutProps } from '@/types/generalSettings';
import BrokersAd = LayoutProps.BrokersAd;
import NavDesktopNew from '@/components/layout/desktop/NavDesktopNew';
import dynamic from 'next/dynamic';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import { LogoCircle } from '@/components/Icons';
import AuthButtons from '@/components/layout/AuthButtons';
import { useSearchModalStore } from '@/stores/layout/searchModalStore';

// The mobile menu is ~1,500 elements; it is only built once the visitor opens it (its links are also in
// the desktop menu and the footer, so crawlers still see them). Its code is fetched when the browser is idle.
const loadNavMobile = () => import('@/components/layout/mobile/NavMobileNew');
const NavMobileNew = dynamic(loadNavMobile, { ssr: false });

interface NavbarProps {
    setLoading: Dispatch<SetStateAction<boolean>>;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isOpen: boolean;
    loading: boolean;
    menuItems?: any[]; // FIX: Relaxed type
    logo?: {
        sourceUrl?: string;
        mediaDetails?: {
            width?: number;
            height?: number;
        };
    };
    brokerAd?: BrokersAd[];
    exchangeForeignAd?: BrokersAd[];
    exchangeIranianAd?: BrokersAd[];
    propAd?: BrokersAd[];
    megaMenuContent?: any; // FIX: Added prop
}

const Navbar: React.FC<NavbarProps> = ({
    setOpen,
    isOpen,
    menuItems,
    brokerAd,
    exchangeForeignAd,
    exchangeIranianAd,
    propAd,
    megaMenuContent,
}) => {
    const pathname = usePathname();
    const isSearchOpen = useSearchModalStore((state) => state.isOpen);
    const [isMenuVisible, setIsMenuVisible] = useState(false);
    // Once opened, the menu stays mounted so reopening is instant and keeps its submenu state
    const [hasOpened, setHasOpened] = useState(false);

    useEffect(() => {
        if (isOpen) setHasOpened(true);
    }, [isOpen]);

    // Warm up the menu code after the page has loaded, so the first open does not wait for it
    useEffect(() => {
        if (window.matchMedia('(min-width: 1280px)').matches) return;
        const idle = window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 2000));
        const cancel = window.cancelIdleCallback || clearTimeout;
        const handle = idle(() => void loadNavMobile());
        return () => cancel(handle as number);
    }, []);

    // Auto-close mobile menu on route change
    useEffect(() => {
        setOpen(false);
    }, [pathname, setOpen]);

    // Close mobile nav when a search result is clicked
    useEffect(() => {
        const handleCloseMenu = () => setOpen(false);
        window.addEventListener('close-mobile-menu', handleCloseMenu);
        return () => window.removeEventListener('close-mobile-menu', handleCloseMenu);
    }, [setOpen]);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            timer = setTimeout(() => {
                setIsMenuVisible(true);
            }, 100);
        } else {
            document.body.style.overflow = '';
            setIsMenuVisible(false);
        }
        return () => clearTimeout(timer);
    }, [isOpen]);

    // If SearchModal was open on top and closes, restore body overflow lock for Navbar
    useEffect(() => {
        if (isOpen && !isSearchOpen) {
            document.body.style.overflow = 'hidden';
        }
    }, [isOpen, isSearchOpen]);
    return (
        <>
            <nav
                className={`${isOpen ? '' : 'translate-x-full'} transition-all pt-3 duration-300 fixed inset-0 w-full overflow-y-hidden bg-white shadow-xl dark:bg-primary-dark px-2 z-[10000000] dark:border-white/5`}
            >
                <div className={'flex flex-col relative size-full pt-1'}>
                    <div
                        className={
                            'flex justify-between px-2 pb-3 border-b dark:border-white/10 items-center'
                        }
                    >
                        <button
                            aria-label='close mobile modal'
                            className={'p-0.5 z-20 cursor-pointer'}
                            onClick={() => setOpen(false)}
                        >
                            <CloseLineIcon size={30} />
                        </button>
                        <div
                            className={`transition-transform absolute start-1/2 translate-x-1/2 duration-500 ${isMenuVisible ? 'translate-y-0 delay-150' : '-translate-y-[400px]'}`}
                        >
                            <Link
                                href='/'
                                onClick={() => setOpen(false)}
                                aria-label={'link to home page'}
                                className='w-fit mx-auto flex items-center justify-center grayscale dark:invert'
                            >
                                <LogoCircle className={'w-11 shrink-0'} />
                            </Link>
                        </div>
                        <div className={'flex gap-x-2 items-center'}>
                            <ModeToggle className='w-fit rounded-full aspect-square' />
                            <AuthButtons authClass={'p-1.5'} />
                        </div>
                    </div>

                    <div
                        className={`relative flex h-full flex-col justify-between overflow-hidden`}
                    >
                        {(isOpen || hasOpened) && (
                            <NavMobileNew
                                brokerAd={brokerAd}
                                exchangeForeignAd={exchangeForeignAd}
                                exchangeIranianAd={exchangeIranianAd}
                                propAd={propAd}
                                isOpen={isOpen}
                                setOpen={setOpen}
                                menuItems={menuItems}
                                megaMenuContent={megaMenuContent}
                            />
                        )}
                    </div>
                </div>
            </nav>

            {/* FIX: Pass megaMenuContent to NavDesktopNew */}
            <NavDesktopNew
                brokerAd={brokerAd}
                exchangeForeignAd={exchangeForeignAd}
                exchangeIranianAd={exchangeIranianAd}
                propAd={propAd}
                setOpen={setOpen}
                menuItems={menuItems}
                megaMenuContent={megaMenuContent}
            />
        </>
    );
};

export default Navbar;
